/**
 * The emoji database behind the Emoji Search tool and the checks on the other emoji
 * tools: every fully-qualified emoji of the current Emoji version, with its group, its
 * CLDR short name, its search keywords and the Emoji version that added it.
 *
 * Read from `public/data/emoji.txt`, built by `scripts/build-emoji-data.mjs` from
 * Unicode's `emoji-test.txt` and the CLDR English annotations (Unicode License v3). The
 * file is fetched once, when the Emoji Search tool first needs it, and the format is
 * documented in the build script. Skin-tone variants are not stored one by one: each
 * emoji has a tone code, and {@link withTone} builds the variant.
 */

export type ToneCode = 0 | 1 | 2;

export interface EmojiEntry {
  /** The fully-qualified emoji, ready to paste. */
  text: string;
  codePoints: number[];
  name: string;
  keywords: string[];
  /** The Emoji version that added it, for example "13.0". */
  since: string;
  group: number;
  tone: ToneCode;
}

export interface EmojiDatabase {
  /** The Emoji version of the data, for example "18.0". */
  version: string;
  groups: string[];
  entries: EmojiEntry[];
}

/** Where `scripts/build-emoji-data.mjs` writes the database. */
export const EMOJI_DATA_URL = "/data/emoji.txt";

/** The five Fitzpatrick skin-tone modifiers, lightest first (U+1F3FB to U+1F3FF). */
export const SKIN_TONES: readonly number[] = [
  0x1_f3_fb, 0x1_f3_fc, 0x1_f3_fd, 0x1_f3_fe, 0x1_f3_ff,
];
const VS16 = 0xfe_0f;
const ZWJ = 0x20_0d;
const LINE_BREAK = /\r?\n/;
const WORD_SPLIT = /[^\p{L}\p{N}]+/u;

function toneCode(value: string): ToneCode {
  if (value === "1") {
    return 1;
  }
  return value === "2" ? 2 : 0;
}

/** Parses the contents of `emoji.txt`. */
export function parseEmojiData(text: string): EmojiDatabase {
  let version = "";
  let groups: string[] = [];
  const entries: EmojiEntry[] = [];
  for (const line of text.split(LINE_BREAK)) {
    if (line === "" || line.startsWith("#")) {
      continue;
    }
    if (line.startsWith("@version ")) {
      version = line.slice("@version ".length);
    } else if (line.startsWith("@groups ")) {
      groups = line.slice("@groups ".length).split("|");
    } else {
      const [
        group = "0",
        hex = "",
        name = "",
        keywords = "",
        since = "",
        tone = "0",
      ] = line.split("\t");
      const codePoints = hex
        .split(".")
        .map((part) => Number.parseInt(part, 16));
      entries.push({
        text: String.fromCodePoint(...codePoints),
        codePoints,
        name,
        keywords: keywords === "" ? [] : keywords.split("|"),
        since,
        group: Number(group),
        tone: toneCode(tone),
      });
    }
  }
  return { version, groups, entries };
}

/**
 * The emoji with a skin tone applied, or the emoji itself when it has none.
 *
 * `tone` is 0 to 4 for light to dark. Tone code 1 puts the modifier after the first
 * character (👋 → 👋🏽); code 2 also puts it after the last, so both people in a couple
 * share it. A variation selector after the first character is dropped, because the
 * modifier makes the emoji presentation certain.
 */
export function withTone(entry: EmojiEntry, tone: number | null): string {
  const modifier = tone === null ? undefined : SKIN_TONES[tone];
  if (entry.tone === 0 || modifier === undefined) {
    return entry.text;
  }
  const [first, ...afterFirst] = entry.codePoints;
  const rest = afterFirst[0] === VS16 ? afterFirst.slice(1) : afterFirst;
  const parts = [first as number, modifier, ...rest];
  if (entry.tone === 2) {
    parts.push(modifier);
  }
  return String.fromCodePoint(...parts);
}

/** `U+1F44B` for one character, as the Unicode Standard writes code points. */
export function formatCodePoint(codePoint: number): string {
  return `U+${codePoint.toString(16).toUpperCase().padStart(4, "0")}`;
}

/** The characters of an emoji sequence, for display: `U+1F469 U+200D U+1F4BB`. */
export function formatCodePoints(text: string): string {
  return Array.from(text, (ch) => formatCodePoint(ch.codePointAt(0) ?? 0)).join(
    " "
  );
}

function codePointsOf(text: string): number[] {
  return Array.from(text, (ch) => ch.codePointAt(0) ?? 0);
}

/** HTML hexadecimal character references: `&#x1F469;&#x200D;&#x1F4BB;`. */
export function htmlEntities(text: string): string {
  return codePointsOf(text)
    .map((cp) => `&#x${cp.toString(16).toUpperCase()};`)
    .join("");
}

/** CSS escapes for a `content` string: `\1F469\200D\1F4BB`. */
export function cssEscape(text: string): string {
  return codePointsOf(text)
    .map((cp) => `\\${cp.toString(16).toUpperCase()}`)
    .join("");
}

/** JavaScript escapes: `\u{1F469}\u{200D}\u{1F4BB}`. */
export function jsEscape(text: string): string {
  return codePointsOf(text)
    .map((cp) => `\\u{${cp.toString(16).toUpperCase()}}`)
    .join("");
}

/** Whether a code point is a joiner or variation selector (shown but not named). */
export function isInvisibleCodePoint(codePoint: number): boolean {
  return codePoint === ZWJ || codePoint === VS16;
}

/* ---------------------------------------------------------------- searching */

export interface SearchOptions {
  /** Index into `database.groups`, or null for all groups. */
  group: number | null;
}

function words(text: string): string[] {
  return text
    .toLowerCase()
    .split(WORD_SPLIT)
    .filter((word) => word.length > 0);
}

/**
 * How well an emoji matches the query words, or 0 for no match. Every query word must
 * start a word of the name or of a keyword; name matches outrank keyword matches, and a
 * whole-name match ranks highest.
 */
export function scoreEmoji(
  entry: EmojiEntry,
  queryWords: readonly string[]
): number {
  if (queryWords.length === 0) {
    return 1;
  }
  const nameWords = words(entry.name);
  const keywordWords = entry.keywords.flatMap(words);
  let score = 0;
  for (const query of queryWords) {
    if (nameWords.includes(query)) {
      score += 10;
    } else if (nameWords.some((word) => word.startsWith(query))) {
      score += 6;
    } else if (keywordWords.includes(query)) {
      score += 4;
    } else if (keywordWords.some((word) => word.startsWith(query))) {
      score += 2;
    } else {
      return 0;
    }
  }
  if (nameWords.join(" ") === queryWords.join(" ")) {
    score += 20;
  }
  return score;
}

/** The emoji matching a query, best match first; ties keep the Unicode order. */
export function searchEmoji(
  database: EmojiDatabase,
  query: string,
  options: SearchOptions = { group: null }
): EmojiEntry[] {
  const queryWords = words(query);
  const scored: { entry: EmojiEntry; score: number; order: number }[] = [];
  database.entries.forEach((entry, order) => {
    if (options.group !== null && entry.group !== options.group) {
      return;
    }
    const score = scoreEmoji(entry, queryWords);
    if (score > 0) {
      scored.push({ entry, score, order });
    }
  });
  scored.sort((a, b) => b.score - a.score || a.order - b.order);
  return scored.map((item) => item.entry);
}

/* ------------------------------------------------------------------ loading */

let cached: Promise<EmojiDatabase> | null = null;

/** Fetches and parses the database once per page. A failed fetch can be retried. */
export function loadEmojiData(): Promise<EmojiDatabase> {
  if (!cached) {
    cached = fetch(EMOJI_DATA_URL)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`emoji data returned ${response.status}`);
        }
        return response.text();
      })
      .then(parseEmojiData)
      .catch((error: unknown) => {
        cached = null;
        throw error;
      });
  }
  return cached;
}
